import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import styled, { keyframes, css } from "styled-components";
import { fetchMarkets, fetchMarketCategories, fetchAnnouncements } from "./client";
import MarketCard from "./MarketCard";
import { useI18n, LANGUAGES } from "./i18n";
import { AnnouncementStack } from "./AnnouncementBanner";
import HeroCanvas from "./HeroCanvas";

/* ─── Animations ─────────────────────────────────────────────────────────── */
const shimmer = keyframes`
  0%   { background-position: -400px 0 }
  100% { background-position:  400px 0 }
`;
const fadeUp   = keyframes`from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}`;
const fadeIn   = keyframes`from{opacity:0}to{opacity:1}`;
const revealUp = keyframes`from{opacity:0;transform:translateY(28px)}to{opacity:1;transform:translateY(0)}`;

/* ─── useReveal — fonctionne aussi sur les éléments rendus conditionnellement ── */
function useReveal(options = {}) {
  const [visible, setVisible] = useState(false);
  const cleanupRef = useRef(null);

  // Callback ref : appelé à chaque fois que l'élément entre/sort du DOM
  const ref = useCallback((el) => {
    // Nettoyer l'observer / les écouteurs précédents
    if (cleanupRef.current) { cleanupRef.current(); cleanupRef.current = null; }
    if (!el) return;

    // "Atteint" = pas sous la ligne de flottaison (dans le viewport OU déjà dépassé).
    // Avant, un élément déjà dépassé (ex. position de scroll restaurée au
    // rafraîchissement) restait invisible (opacity:0) tant qu'on ne remontait pas.
    const reached = () => el.getBoundingClientRect().top < window.innerHeight;

    if (reached()) { setVisible(true); return; }

    let cleanup;
    let done = false;
    const finish = () => { if (done) return; done = true; setVisible(true); cleanup(); };

    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) finish(); },
      { threshold: 0.06, rootMargin: "0px 0px -30px 0px", ...options }
    );
    observer.observe(el);

    // Filet de sécurité : au rafraîchissement, `user` vaut null le temps que la
    // session soit restaurée → le Hero (affiché si !user) se monte puis se démonte
    // et décale toute la page sans scroll. On revérifie donc aussi au scroll, au
    // resize et périodiquement, au lieu de dépendre uniquement de l'observer.
    const recheck = () => { if (reached()) finish(); };
    window.addEventListener("scroll", recheck, { passive: true });
    window.addEventListener("resize", recheck);
    const timer = setInterval(recheck, 400);

    cleanup = () => {
      observer.disconnect();
      window.removeEventListener("scroll", recheck);
      window.removeEventListener("resize", recheck);
      clearInterval(timer);
    };
    cleanupRef.current = cleanup;
  }, []);

  return [ref, visible];
}

/* ─── Mixin CSS pour les sections révélées ───────────────────────────────── */
const revealStyle = css`
  opacity: 0;
  transform: translateY(24px);
  transition: opacity .55s cubic-bezier(.16,1,.3,1),
              transform .55s cubic-bezier(.16,1,.3,1);
  &.visible {
    opacity: 1;
    transform: translateY(0);
  }
`;
const slideUp  = keyframes`from{transform:translateY(100%)}to{transform:translateY(0)}`;
const pulse    = keyframes`0%,100%{opacity:.4}50%{opacity:1}`;
const bounceY   = keyframes`0%,100%{transform:translateY(0)}50%{transform:translateY(2px)}`;

/* Breakpoint */
const MOBILE = "740px";

/* ─── Layout ─────────────────────────────────────────────────────────────── */
const Wrapper = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 2rem 2rem 4rem;
  margin-top: 40px;
  @media(max-width:${MOBILE}){ padding: 0rem 1rem 4rem; }
`;

/* ─── Hero ───────────────────────────────────────────────────────────────── */
const Hero = styled.div`
  padding: 3rem 0 2rem;
  border-bottom: 1px solid ${({theme}) => theme.border};
  margin-bottom: 2rem;
  animation: ${fadeUp} .4s ease both;
`;
const HeroTitle = styled.h1`
  font-family: ${({theme}) => theme.fontDisplay};
  font-size: clamp(1.8rem, 4vw, 3rem);
  font-weight: 700; line-height: 1.1; letter-spacing: -0.03em;
  color: ${({theme}) => theme.text}; margin-bottom: 0.5rem;
  span { color: ${({theme}) => theme.green}; }
`;
const HeroSub = styled.p`
  font-size: 0.85rem; color: ${({theme}) => theme.textMuted};
  max-width: 520px; line-height: 1.65;
`;
const EVPCallout = styled.div`
  display:inline-flex;align-items:center;gap:.5rem;
  margin-top:.85rem;padding:.4rem .9rem;border-radius:100px;
  border:1px solid rgba(0,230,118,.25);background:rgba(0,230,118,.07);
  font-size:.72rem;color:${({theme})=>theme.textDim};
  strong{color:${({theme})=>theme.green};}
`;
const ReferralCallout = styled.div`
  display: inline-flex;
  align-items: center;
  gap: .5rem;
  flex-wrap: wrap;
  margin-top: .85rem;
  padding: .4rem .9rem;
  border-radius: 100px;
  border: 1px solid rgba(79,195,247,.25);
  background: rgba(79,195,247,.07);
  font-size: .72rem;
  color: ${({theme}) => theme.textDim};
  cursor: pointer;
  transition: all .2s;
  outline: none;

  strong { color: ${({theme}) => theme.accent}; }

  &:hover {
    border-color: rgba(79,195,247,.5);
    background: rgba(79,195,247,.12);
    transform: translateY(-1px);
  }

  &:active { transform: translateY(0); }

  &:focus-visible {
    box-shadow: 0 0 0 3px rgba(79,195,247,.25);
  }
`;

const ReferralLink = styled.span`
  font-family: ${({theme}) => theme.fontMono};
  font-size: .65rem;
  color: ${({theme}) => theme.accent};
  font-weight: 600;
  letter-spacing: .03em;
  white-space: nowrap;
  padding-left: .4rem;
  border-left: 1px solid rgba(79,195,247,.3);
  margin-left: .2rem;
`;
const Stats = styled.div`display:flex;gap:2.5rem;margin-top:1.5rem;flex-wrap:wrap;${revealStyle}`;
const Stat = styled.div`display:flex;flex-direction:column;gap:.2rem;`;
const StatVal   = styled.span`font-family:${({theme})=>theme.fontMono};font-size:1.1rem;font-weight:500;color:${({theme})=>theme.text};`;
const StatLabel = styled.span`font-size:.68rem;text-transform:uppercase;letter-spacing:.08em;color:${({theme})=>theme.textMuted};`;
const StatPartial = styled.sup`
  font-family:${({theme})=>theme.fontMono};font-size:.55rem;
  color:${({theme})=>theme.accent};font-weight:600;margin-left:.2rem;
  cursor:help;
`;

/* ─── Controls ───────────────────────────────────────────────────────────── */
const Controls = styled.div`display:flex;align-items:center;gap:1rem;margin-bottom:1rem;flex-wrap:wrap;${revealStyle}`;
const SearchBox   = styled.div`flex:1;min-width:200px;max-width:340px;position:relative;`;
const SearchInput = styled.input`
  width:100%;background:${({theme})=>theme.bgCard};border:1px solid ${({theme})=>theme.border};
  border-radius:8px;color:${({theme})=>theme.text};font-size:.8rem;
  padding:.5rem 1rem .5rem 2.2rem;outline:none;transition:border-color .15s;
  &:focus{border-color:${({theme})=>theme.borderLight};}
  &::placeholder{color:${({theme})=>theme.textMuted};}
`;
const SearchIcon = styled.span`position:absolute;left:.7rem;top:50%;transform:translateY(-50%);color:${({theme})=>theme.textMuted};font-size:.85rem;`;
const SearchSpinner = styled.span`
  position:absolute;right:.7rem;top:50%;transform:translateY(-50%);
  width:10px;height:10px;border-radius:50%;
  background:${({theme})=>theme.accent};
  animation:${pulse} 1s ease infinite;
`;
const Filters   = styled.div`display:flex;gap:.4rem;flex-wrap:wrap;`;
const FilterChip= styled.button`
  font-family:${({theme})=>theme.fontMono};font-size:.68rem;padding:.3rem .75rem;
  border-radius:100px;letter-spacing:.04em;cursor:pointer;transition:all .15s;
  border:1px solid ${({$a,theme})=>$a?theme.green+"99":theme.border};
  background:${({$a,theme})=>$a?theme.greenDim:"transparent"};
  color:${({$a,theme})=>$a?theme.green:theme.textDim};
  &:hover{border-color:${({theme})=>theme.borderLight};color:${({theme})=>theme.text};}
`;

const SortBar    = styled.div`
  display:flex;align-items:center;gap:.5rem;margin-left:auto;
  @media(max-width:${MOBILE}){ display:none; }
`;
const SortLabel  = styled.span`font-size:.7rem;color:${({theme})=>theme.textMuted};text-transform:uppercase;letter-spacing:.08em;`;
const SortSelect = styled.select`
  background:${({theme})=>theme.bgCard};border:1px solid ${({theme})=>theme.border};
  color:${({theme})=>theme.text};font-size:.75rem;font-family:${({theme})=>theme.fontBody};
  padding:.3rem .6rem;border-radius:6px;outline:none;cursor:pointer;
  option{background:${({theme})=>theme.bgCard};}
`;

const FilterFab = styled.button`
  display:none;
  @media(max-width:${MOBILE}){
    display:inline-flex;align-items:center;gap:.4rem;margin-left:auto;
    font-family:${({theme})=>theme.fontMono};font-size:.7rem;
    padding:.45rem .85rem;border-radius:8px;
    background:${({theme})=>theme.bgCard};
    border:1px solid ${({$active,theme})=>$active?theme.accent+"99":theme.border};
    color:${({$active,theme})=>$active?theme.accent:theme.text};
    cursor:pointer;transition:all .15s;letter-spacing:.05em;
    &:hover{border-color:${({theme})=>theme.borderLight};}
  }
`;
const FabBadge = styled.span`
  font-family:${({theme})=>theme.fontMono};font-size:.6rem;
  background:${({theme})=>theme.accent};color:#000;
  border-radius:100px;padding:.05rem .4rem;font-weight:700;
`;

const CategoryBar = styled.div`
  display:flex;align-items:center;gap:.6rem;margin-bottom:1.5rem;flex-wrap:wrap;padding:.3rem 0;
  @media(max-width:${MOBILE}){ display:none; }
  ${revealStyle}
`;
const CategoryLabel = styled.span`
  font-family:${({theme})=>theme.fontMono};font-size:.62rem;
  text-transform:uppercase;letter-spacing:.08em;
  color:${({theme})=>theme.textMuted};flex-shrink:0;
`;
const CategoryChips = styled.div`display:flex;gap:.4rem;flex-wrap:wrap;flex:1;`;
const CategoryChip = styled.button`
  display:inline-flex;align-items:center;gap:.35rem;
  font-family:${({theme})=>theme.fontBody};font-size:.74rem;font-weight:500;
  padding:.32rem .8rem;border-radius:100px;cursor:pointer;
  transition:all .15s;line-height:1;
  border:1px solid ${({$a,theme})=>$a?theme.accent+"99":theme.border};
  background:${({$a,theme})=>$a?"rgba(79,195,247,.1)":theme.bgCard};
  color:${({$a,theme})=>$a?theme.accent:theme.textDim};
  &:hover{border-color:${({theme})=>theme.borderLight};color:${({theme})=>theme.text};}
`;
const ChipEmoji = styled.span`font-size:.9rem;line-height:1;`;
const ChipCount = styled.span`
  font-family:${({theme})=>theme.fontMono};font-size:.6rem;
  background:${({$a,theme})=>$a?"rgba(79,195,247,.18)":theme.bgHover};
  border-radius:100px;padding:.05rem .4rem;
  color:${({$a,theme})=>$a?theme.accent:theme.textMuted};
  margin-left:.1rem;
`;

/* ─── Filter drawer (mobile bottom sheet) ────────────────────────────────── */
const DrawerOverlay = styled.div`
  position:fixed;inset:0;z-index:300;background:rgba(0,0,0,.65);
  backdrop-filter:blur(4px);animation:${fadeIn} .2s ease;
  display:flex;align-items:flex-end;justify-content:center;
`;
const Drawer = styled.div`
  width:100%;max-width:560px;
  background:${({theme})=>theme.bgCard};
  border-top-left-radius:18px;border-top-right-radius:18px;
  border:1px solid ${({theme})=>theme.borderLight};border-bottom:none;
  max-height:85vh;overflow-y:auto;
  animation:${slideUp} .25s cubic-bezier(.32,.72,0,1);
`;
const DrawerGrab = styled.div`
  width:36px;height:4px;background:${({theme})=>theme.border};
  border-radius:100px;margin:.6rem auto 0;
`;
const DrawerHead = styled.div`
  display:flex;align-items:center;justify-content:space-between;
  padding:.75rem 1.25rem .5rem;
`;
const DrawerTitle = styled.h2`
  font-family:${({theme})=>theme.fontDisplay};font-size:1.1rem;font-weight:700;
  letter-spacing:-.02em;
`;
const DrawerClose = styled.button`
  background:none;border:none;color:${({theme})=>theme.textMuted};
  font-size:1.1rem;cursor:pointer;padding:.25rem .5rem;
  &:hover{color:${({theme})=>theme.text};}
`;
const DrawerSection = styled.div`padding:.85rem 1.25rem;border-top:1px solid ${({theme})=>theme.border};`;
const SectionLabel = styled.div`
  font-family:${({theme})=>theme.fontMono};font-size:.62rem;
  text-transform:uppercase;letter-spacing:.08em;
  color:${({theme})=>theme.textMuted};margin-bottom:.7rem;
`;
const DrawerChips = styled.div`display:flex;gap:.4rem;flex-wrap:wrap;`;
const DrawerFoot = styled.div`
  position:sticky;bottom:0;background:${({theme})=>theme.bgCard};
  border-top:1px solid ${({theme})=>theme.border};
  padding:.85rem 1.25rem;display:flex;gap:.5rem;
`;
const DrawerClearBtn = styled.button`
  flex:1;padding:.65rem;border-radius:8px;
  border:1px solid ${({theme})=>theme.border};background:transparent;
  color:${({theme})=>theme.textDim};font-family:${({theme})=>theme.fontBody};
  font-size:.8rem;font-weight:600;cursor:pointer;transition:all .15s;
  &:hover{color:${({theme})=>theme.text};border-color:${({theme})=>theme.borderLight};}
`;
const DrawerApplyBtn = styled.button`
  flex:2;padding:.65rem;border-radius:8px;border:none;
  background:${({theme})=>theme.accent};color:#000;
  font-family:${({theme})=>theme.fontBody};font-size:.8rem;font-weight:700;
  cursor:pointer;transition:opacity .15s;
  &:hover{opacity:.88;}
`;

/* ─── Grid ───────────────────────────────────────────────────────────────── */
const Grid = styled.div`display:grid;grid-template-columns:repeat(auto-fill,minmax(300px,1fr));gap:1rem;`;
const Empty = styled.div`text-align:center;padding:4rem 0;color:${({theme})=>theme.textMuted};font-size:.85rem;grid-column:1/-1;`;
const Skeleton = styled.div`
  height:220px;border-radius:12px;
  background:linear-gradient(90deg,${({theme})=>theme.bgCard} 25%,${({theme})=>theme.bgHover} 50%,${({theme})=>theme.bgCard} 75%);
  background-size:400px 100%;animation:${shimmer} 1.4s infinite linear;
`;
const EVPBadge = styled.span`
  font-family:${({theme})=>theme.fontMono};font-size:.62rem;font-weight:700;
  color:${({theme})=>theme.green};background:${({theme})=>theme.greenDim};
  border:1px solid ${({theme})=>theme.green}33;border-radius:4px;padding:.05rem .35rem;
`;

/* ─── Show More zone ─────────────────────────────────────────────────────── */
const ShowMoreWrap = styled.div`
  display:flex;flex-direction:column;align-items:center;gap:.85rem;
  margin-top:2.5rem;padding:1.5rem 1rem;
  animation:${bounceY} 1.8s ease-in-out infinite;
    transition:opacity .2s;

`;
const ShowMoreCount = styled.div`
  font-family:${({theme})=>theme.fontMono};font-size:.72rem;
  color:${({theme})=>theme.text};letter-spacing:.04em;
  text-align:center;
  strong{color:${({theme})=>theme.text};font-weight:600;}
`;
const ShowMoreProgress = styled.div`
  width:220px;height:3px;border-radius:100px;
  background:${({theme})=>theme.border};overflow:hidden;
  position:relative;
`;
const ShowMoreProgressFill = styled.div`
  height:100%;width:${({$pct})=>$pct}%;
  background:linear-gradient(90deg,${({theme})=>theme.green},${({theme})=>theme.accent});
  border-radius:100px;transition:width .4s cubic-bezier(.4,0,.2,1);
`;
const ShowMoreBtn = styled.button`
  display:inline-flex;align-items:center;gap:.55rem;
  padding:.75rem 1.6rem;border-radius:100px;
  background:${({theme})=>theme.bgCard};
  border:1px solid ${({theme})=>theme.border};
  color:${({theme})=>theme.accent};
  font-family:${({theme})=>theme.fontBody};font-size:.85rem;font-weight:600;
  cursor:pointer;transition:all .2s;letter-spacing:.02em;
  &:hover:not(:disabled){
    background:${({theme})=>theme.bgHover};
    border-color:${({theme})=>theme.green};
    color:${({theme})=>theme.green};
    transform:translateY(-1px);
  }
  &:active:not(:disabled){transform:translateY(0);}
  &:disabled{opacity:.6;cursor:wait;}
`;
const ShowMoreArrow = styled.span`
  display:inline-block;transition:transform .25s;
  ${ShowMoreBtn}:hover:not(:disabled) &{transform:translateY(3px);}
`;
const ShowAllBtn = styled.button`
  background:none;border:none;
  font-family:${({theme})=>theme.fontMono};font-size:.66rem;
  color:${({theme})=>theme.textMuted};
  letter-spacing:.05em;cursor:pointer;
  text-decoration:underline;text-underline-offset:3px;
  padding:.3rem .5rem;
  &:hover{color:${({theme})=>theme.text};}
  &:disabled{opacity:.5;cursor:wait;}
`;
const EndOfList = styled.div`
  display:flex;align-items:center;justify-content:center;gap:.7rem;
  margin-top:2.5rem;padding:1.2rem 0;
  font-family:${({theme})=>theme.fontMono};font-size:.7rem;
  color:${({theme})=>theme.textMuted};
  letter-spacing:.05em;
  &::before,&::after{
    content:"";flex:1;max-width:90px;height:1px;
    background:${({theme})=>theme.border};
  }
`;
const ErrorBanner = styled.div`
  background:#ff3b5c11;border:1px solid #ff3b5c44;
  border-radius:8px;padding:.65rem .9rem;margin:1rem 0;
  font-size:.78rem;color:#ff3b5c;text-align:center;
`;

/* ─── Emoji map ──────────────────────────────────────────────────────────── */
const EMOJI_MAP = {
  Politics:"🏛️", Politique:"🏛️",
  Sports:"⚽", Crypto:"₿", Finance:"📈", Tech:"💻",
  Science:"🔬", Entertainment:"🎬", Other:"🌐",
};

/* ─── Translations ───────────────────────────────────────────────────────── */
const TX = {
  "hero.title1":  { fr:"Pariez sur l'",         en:"Predict the",        es:"Predice el",         de:"Wetten Sie auf das", pt:"Preveja o",        ar:"تنبأ بـ"           },
  "hero.title2":  { fr:"événement.",             en:"event.",              es:"evento.",            de:"Ereignis.",          pt:"evento.",          ar:"الحدث."            },
  "hero.sub":     { fr:"Tradez vos convictions sur des événements réels avec des EVP — la monnaie virtuelle d'EVPTrading. Chaque part est un pari, chaque bonne prédiction génère des récompenses réelles.",
                    en:"Trade your convictions on real-world events with EVP —EVPTrading's virtual currency. Each share is a bet, each correct prediction generates real rewards.",
                    es:"Opera con tus convicciones sobre eventos reales con EVP — la moneda virtual de EVPTrading. Cada parte es una apuesta, cada predicción correcta genera recompensas reales.",
                    de:"Handeln Sie Ihre Überzeugungen zu realen Ereignissen mit EVP —EVPTradings virtueller Währung. Jeder Share ist eine Wette, jede richtige Prognose generiert echte Belohnungen.",
                    pt:"Negocie suas convicções sobre eventos reais com EVP — a moeda virtual da EVPTrading. Cada parte é uma aposta, cada previsão correta gera recompensas reais.",
                    ar:"تداول قناعاتك على أحداث حقيقية باستخدام EVP — العملة الافتراضية لـEVPTrading. كل حصة رهان، وكل تنبؤ صحيح يولّد مكافآت حقيقية."  },
  "hero.callout": { fr:<><strong>100 EVP offerts</strong> à l'inscription · 1$ par 100 EVP gagnés · Aucun dépôt requis</>,
                    en:<><strong>100 EVP offered</strong> at signup · $1 per 100 EVP won · No deposit required</>,
                    es:<><strong>100 EVP gratis</strong> al registrarte · $1 por 100 EVP ganados · Sin depósito</>,
                    de:<><strong>100 EVP geschenkt</strong> bei Registrierung · 1$ je 100 EVP · Keine Einzahlung</>,
                    pt:<><strong>100 EVP grátis</strong> no cadastro · $1 por 100 EVP ganhos · Sem depósito</>,
                    ar:<><strong>100 EVP مجاناً</strong> عند التسجيل · 1$ لكل 100 EVP · لا إيداع</> },
  
                    "stat.volume":  { fr:"Volume total EVP",       en:"Total EVP volume",    es:"Volumen EVP total",  de:"Gesamt EVP-Volumen", pt:"Volume EVP total", ar:"إجمالي حجم EVP"   },
  
  "hero.callout.user": {
    fr: <><strong>Invitez vos amis</strong> et touchez <strong>5%</strong> de commission sur leurs trades.</>,
    en: <><strong>Invite friends</strong> and earn <strong>5%</strong> commission on their trades.</>,
    es: <><strong>Invita a tus amigos</strong> y gana <strong>5%</strong> de comisión sobre sus trades.</>,
    de: <><strong>Lade Freunde ein</strong> und verdiene <strong>5%</strong> Provision auf ihre Trades.</>,
    pt: <><strong>Convide seus amigos</strong> e ganhe <strong>5%</strong> de comissão sobre seus trades.</>,
    ar: <><strong>ادعُ أصدقاءك</strong> واحصل على عمولة <strong>5%</strong> من تداولاتهم.</>,
  },
  "hero.callout.link": {
    fr: "Paramètres → Parrainage",
    en: "Settings → Referrals",
    es: "Configuración → Referidos",
    de: "Einstellungen → Empfehlungen",
    pt: "Configurações → Indicações",
    ar: "الإعدادات ← الإحالات",
  },
   "stat.open":    { fr:"Marchés ouverts",        en:"Open markets",        es:"Mercados abiertos",  de:"Offene Märkte",      pt:"Mercados abertos", ar:"أسواق مفتوحة"     },
  "stat.total":   { fr:"Tous les marchés",       en:"All markets",         es:"Todos los mercados", de:"Alle Märkte",        pt:"Todos os mercados",ar:"جميع الأسواق"     },
  "stat.partial": { fr:"Statistiques calculées sur les marchés actuellement chargés", en:"Stats computed from markets loaded so far", es:"Estadísticas calculadas con los mercados cargados", de:"Statistik aus bisher geladenen Märkten berechnet", pt:"Estatísticas calculadas com base nos mercados carregados", ar:"إحصائيات محسوبة من الأسواق المحمَّلة حتى الآن" },
  "search.ph":    { fr:"Rechercher un marché…",  en:"Search markets…",     es:"Buscar un mercado…", de:"Märkte suchen…",     pt:"Pesquisar mercados…",ar:"البحث عن سوق…"  },
  "f.all":        { fr:"TOUS",                   en:"ALL",                 es:"TODOS",              de:"ALLE",               pt:"TODOS",            ar:"الكل"              },
  "f.open":       { fr:"OUVERT",                 en:"OPEN",                es:"ABIERTO",            de:"OFFEN",              pt:"ABERTO",           ar:"مفتوح"             },
  "f.closed":     { fr:"FERMÉ",                  en:"CLOSED",              es:"CERRADO",            de:"GESCHLOSSEN",        pt:"FECHADO",          ar:"مغلق"              },
  "f.resolved":   { fr:"RÉSOLU",                 en:"RESOLVED",            es:"RESUELTO",           de:"AUFGELÖST",          pt:"RESOLVIDO",        ar:"محسوم"             },
  "sort.label":   { fr:"Trier",                  en:"Sort",                es:"Ordenar",            de:"Sortieren",          pt:"Ordenar",          ar:"ترتيب"             },
  "sort.volume":  { fr:"Volume",                 en:"Volume",              es:"Volumen",            de:"Volumen",            pt:"Volume",           ar:"الحجم"             },
  "sort.newest":  { fr:"Plus récent",            en:"Newest",              es:"Más reciente",       de:"Neueste",            pt:"Mais recente",     ar:"الأحدث"            },
  "sort.closing": { fr:"Fermeture proche",       en:"Closing soon",        es:"Cierre próximo",     de:"Bald schließend",    pt:"Fechando em breve",ar:"يغلق قريباً"       },
  "cat.label":    { fr:"Catégorie :",            en:"Category:",           es:"Categoría:",         de:"Kategorie:",         pt:"Categoria:",       ar:":الفئة"            },
  "cat.all":      { fr:"Toutes",                 en:"All",                 es:"Todas",              de:"Alle",               pt:"Todas",            ar:"الكل"              },
  "empty":        { fr:"Aucun marché trouvé pour ces filtres.",en:"No markets found matching your filters.",es:"No se encontraron mercados con estos filtros.",de:"Keine Märkte für diese Filter gefunden.",pt:"Nenhum mercado encontrado com estes filtros.",ar:"لا توجد أسواق تطابق هذه المرشحات." },
  "filter.btn":   { fr:"Filtres",                en:"Filters",             es:"Filtros",            de:"Filter",             pt:"Filtros",          ar:"المرشحات"          },
  "filter.title": { fr:"Filtrer & trier",        en:"Filter & sort",       es:"Filtrar y ordenar",  de:"Filtern & sortieren",pt:"Filtrar e ordenar",ar:"تصفية وترتيب"     },
  "filter.clear": { fr:"Réinitialiser",          en:"Reset",               es:"Restablecer",        de:"Zurücksetzen",       pt:"Redefinir",        ar:"إعادة تعيين"       },
  "filter.apply": { fr:"Appliquer",              en:"Apply",               es:"Aplicar",            de:"Anwenden",           pt:"Aplicar",          ar:"تطبيق"             },
  "loaderror":    { fr:"Erreur de chargement. Affichage des marchés de démo.", en:"Loading error. Showing demo markets.", es:"Error de carga. Mostrando mercados demo.", de:"Ladefehler. Demo-Märkte werden angezeigt.", pt:"Erro de carregamento. Exibindo mercados demo.", ar:"خطأ في التحميل. عرض أسواق تجريبية." },

  /* ─── Show More ─── */
  "showmore.label":     { fr:"marchés affichés",     en:"markets shown",      es:"mercados mostrados", de:"Märkte angezeigt",  pt:"mercados exibidos",  ar:"سوقًا معروضًا"  },
  "showmore.btn":       { fr:"Voir plus de marchés", en:"Show more markets",  es:"Ver más mercados",   de:"Mehr Märkte zeigen",pt:"Ver mais mercados",  ar:"عرض المزيد من الأسواق" },
  "showmore.all":       { fr:"Tout afficher",        en:"Show all",           es:"Mostrar todo",       de:"Alle anzeigen",     pt:"Mostrar tudo",       ar:"عرض الكل"       },
  "showmore.end":       { fr:"Fin de la liste",      en:"End of list",        es:"Fin de la lista",    de:"Ende der Liste",    pt:"Fim da lista",       ar:"نهاية القائمة"  },
  "showmore.loading":   { fr:"Chargement…",          en:"Loading…",           es:"Cargando…",          de:"Wird geladen…",     pt:"Carregando…",        ar:"جاري التحميل…" },
};

function useT(lang) {
  return (k) => {
    const v = TX[k];
    if (!v) return k;
    return v[lang] ?? v.fr ?? k;
  };
}

const STATUS_KEYS  = ["ALL", "OPEN", "CLOSED", "RESOLVED"];
const STATUS_I18N  = ["f.all", "f.open", "f.closed", "f.resolved"];

/* ── Pagination "Voir plus" ── */
const INITIAL_VISIBLE = 6;   // Marchés au premier load
const PAGE_SIZE       = 6;   // +6 à chaque clic sur "Voir plus"

/* ── Mapping tri front → ordering DRF ── */
const SORT_MAP = {
  volume:  "-volume",
  newest:  "-created_at",
  closing: "close_date",
};

const MOCK_MARKETS = [
  { id:1, title:"Bitcoin dépassera-t-il 100K$ avant fin 2026 ?",               category_name:"Crypto",   status:"OPEN",     volume:"2430000", image_url:"", outcomes:[{id:1,name:"Oui",probability:"0.72"},{id:2,name:"Non",probability:"0.28"}] },
  { id:2, title:"L'IA approuvée par la FDA en médecine avant 2026 ?",           category_name:"Tech",     status:"OPEN",     volume:"860000",  image_url:"", outcomes:[{id:3,name:"Oui",probability:"0.61"},{id:4,name:"Non",probability:"0.39"}] },
  { id:3, title:"Champions League 2024/25 — Le Real Madrid gagnera-t-il ?",    category_name:"Sports",   status:"OPEN",     volume:"1100000", image_url:"", outcomes:[{id:5,name:"Oui",probability:"0.34"},{id:6,name:"Non",probability:"0.66"}] },
  { id:4, title:"La Fed baissera-t-elle ses taux en Q1 2026 ?",                 category_name:"Finance",  status:"CLOSED",   volume:"3200000", image_url:"", outcomes:[{id:7,name:"Oui",probability:"0.58"},{id:8,name:"Non",probability:"0.42"}] },
  { id:5, title:"Ethereum aura-t-il plus de 100M de validateurs en 2026 ?",     category_name:"Crypto",   status:"OPEN",     volume:"890000",  image_url:"", outcomes:[{id:9,name:"Oui",probability:"0.81"},{id:10,name:"Non",probability:"0.19"}] },
  { id:6, title:"Élections US 2024 — Le candidat démocrate l'emportera-t-il ?", category_name:"Politique",status:"RESOLVED", volume:"8200000", image_url:"", outcomes:[{id:11,name:"Oui",probability:"0.45"},{id:12,name:"Non",probability:"0.55"}] },
];

/* ═══════════════════════════════════════════════════════════════════════════
   SUB-COMPONENT — Filter drawer (mobile bottom sheet)
═══════════════════════════════════════════════════════════════════════════ */
function FilterDrawer({
  open, onClose, t,
  category, setCategory, categoryList, totalInPool,
  sort, setSort,
  onReset,
}) {
  if (!open) return null;

  const SORT_OPTIONS = [
    { val:"volume",  label:t("sort.volume")  },
    { val:"newest",  label:t("sort.newest")  },
    { val:"closing", label:t("sort.closing") },
  ];

  return (
    <DrawerOverlay onClick={e => e.target === e.currentTarget && onClose()}>
      <Drawer>
        <DrawerGrab/>
        <DrawerHead>
          <DrawerTitle>{t("filter.title")}</DrawerTitle>
          <DrawerClose onClick={onClose}>✕</DrawerClose>
        </DrawerHead>

        {categoryList.length > 0 && (
          <DrawerSection>
            <SectionLabel>{t("cat.label")}</SectionLabel>
            <DrawerChips>
              <CategoryChip $a={category === "ALL"} onClick={() => setCategory("ALL")}>
                <ChipEmoji>✨</ChipEmoji>
                {t("cat.all")}
                <ChipCount $a={category === "ALL"}>{totalInPool}</ChipCount>
              </CategoryChip>
              {categoryList.map(c => (
                <CategoryChip
                  key={c.name_source || c.name}
                  $a={category === (c.name_source || c.name)}
                  onClick={() => setCategory(c.name_source || c.name)}
                >
                  <ChipEmoji>{EMOJI_MAP[c.name_source || c.name] || "🎯"}</ChipEmoji>
                  {c.name}
                  <ChipCount $a={category === (c.name_source || c.name)}>{c.count}</ChipCount>
                </CategoryChip>
              ))}
            </DrawerChips>
          </DrawerSection>
        )}

        <DrawerSection>
          <SectionLabel>{t("sort.label")}</SectionLabel>
          <DrawerChips>
            {SORT_OPTIONS.map(opt => (
              <CategoryChip
                key={opt.val}
                $a={sort === opt.val}
                onClick={() => setSort(opt.val)}
              >
                {opt.label}
              </CategoryChip>
            ))}
          </DrawerChips>
        </DrawerSection>

        <DrawerFoot>
          <DrawerClearBtn onClick={onReset}>{t("filter.clear")}</DrawerClearBtn>
          <DrawerApplyBtn onClick={onClose}>{t("filter.apply")}</DrawerApplyBtn>
        </DrawerFoot>
      </Drawer>
    </DrawerOverlay>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════════════════ */
export default function MarketList({ user, onSelectMarket, onTradeMarket, onNavigate, onLoginClick }) {  const { lang, setLang } = useI18n();
  const t   = useT(lang);
  const dir = lang === "ar" ? "rtl" : "ltr";
const abortRef = React.useRef(null);

  /* ── Scroll reveal refs ── */
  const [statsRef,    statsVisible]    = useReveal();
  const [controlsRef, controlsVisible] = useReveal({ rootMargin: "0px 0px -20px 0px" });
  const [categoryRef, categoryVisible] = useReveal({ rootMargin: "0px 0px -20px 0px" });
  const [gridRef,     gridVisible]     = useReveal({ threshold: 0.04 });

  /* ── Filter state ── */
  const [search,     setSearch]     = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [status,     setStatus]     = useState("OPEN");
  const [category,   setCategory]   = useState("ALL");
  const [sort,       setSort]       = useState("volume");
  const [drawerOpen, setDrawerOpen] = useState(false);

  /* ── Pagination state ── */
  const [markets,      setMarkets]      = useState([]);
  const [totalCount,   setTotalCount]   = useState(0);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);
  const [loading,      setLoading]      = useState(true);
  const [loadingMore,  setLoadingMore]  = useState(false);
  const [searching,    setSearching]    = useState(false);
  const [usingMock,    setUsingMock]    = useState(false);

/* ── Annonces actives (chargées depuis l'API) ── */
  const [announcements, setAnnouncements] = useState([]);

  /* ── Category stats (compteurs réels depuis le backend) ── */
const [categoryStats, setCategoryStats] = useState({
  total:          0,
  open_count:     0,
  closed_count:   0,
  resolved_count: 0,
  total_volume:   0,
  filtered_total: 0,
  categories:     [],
});
  /* ── Build query params for DRF backend ── */
const buildParams = useCallback((offset, limit) => ({
  limit,
  offset,
  ordering: SORT_MAP[sort],
  lang,                                                            // ← AJOUT
  ...(status   !== "ALL" && { status }),
  ...(category !== "ALL" && { category_name: category }),
  ...(debouncedSearch     && { search: debouncedSearch }),
}), [sort, status, category, debouncedSearch, lang]);             // ← AJOUT lang

  /* ── Load function ── */
  const load = useCallback(async ({ append = false, offset = 0, limit = PAGE_SIZE } = {}) => {
      if (!append && abortRef.current) {
    abortRef.current.abort();
  }
  const controller = !append ? new AbortController() : null;
  if (controller) abortRef.current = controller;
    if (append) setLoadingMore(true); else setLoading(true);
    try {
      const res  = await fetchMarkets(buildParams(offset, limit), {
      signal: controller?.signal,
    });
      const data = res.data;
      const items = data.results || data;
      const total = typeof data.count === "number" ? data.count : items.length;
      setMarkets(prev => append ? [...prev, ...items] : items);
      setTotalCount(total); 
          if (!append && data.open_count !== undefined) {
      setCategoryStats(prev => ({
        ...prev,
        total:          data.count          || 0,
        open_count:     data.open_count     || 0,
        closed_count:   data.closed_count   || 0,
        resolved_count: data.resolved_count || 0,
        total_volume:   parseFloat(data.total_volume) || 0,
        filtered_total: data.filtered_count || data.count || 0,
      }));
    }
      setUsingMock(false);
    } catch (err) {
    if (err.name === "CanceledError" || err.code === "ERR_CANCELED") {
      // Requête annulée volontairement, ne rien faire
      return;
    }
      if (!append) {
        // Initial load failed → fallback mocks
        setMarkets(MOCK_MARKETS);
        setTotalCount(MOCK_MARKETS.length);
        setUsingMock(true);
      }
      // append failed → just stop, keep existing

      
    } finally {
      if (append) setLoadingMore(false); else setLoading(false);
    }
  }, [buildParams]);


/* ── Charger les compteurs de catégorie depuis le backend ──
   Endpoint séparé qui renvoie le breakdown { categories: [...] }
   avec noms traduits selon ?lang= */
const loadCategoryStats = useCallback(async () => {
  try {
    const params = {
      lang,
      ...(status !== "ALL"  && { status }),
      ...(debouncedSearch    && { search: debouncedSearch }),
    };
    const res = await fetchMarketCategories(params);
    const d = res.data || {};
    setCategoryStats(prev => ({
      ...prev,                                 // ← garde les stats globales venues de /markets/
      categories: d.categories || [],          // ← n'écrase QUE la liste des catégories
    }));
  } catch (err) {
    console.warn("Stats fetch failed:", err);
  }
}, [status, debouncedSearch, lang]);

useEffect(() => {
  loadCategoryStats();
}, [loadCategoryStats]);


  useEffect(() => {
    fetchAnnouncements()
      .then(res => setAnnouncements(res.data || []))
      .catch(err => {
        console.warn("Impossible de charger les annonces :", err);
        setAnnouncements([]);
      });
  }, []);



  /* ── Debounce search input (300ms) ── */
  useEffect(() => {
    if (search !== debouncedSearch) setSearching(true);
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setSearching(false);
    }, 300);
    return () => clearTimeout(t);
  }, [search, debouncedSearch]);

  /* ── Refetch from offset 0 whenever filters change ── */
/* ── Refetch from offset 0 whenever filters or language change ── */
useEffect(() => {
  setVisibleCount(INITIAL_VISIBLE);    // reset le slice client-side
  load({ append: false, offset: 0 });
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [status, category, sort, debouncedSearch, lang]);              // ← AJOUT lang

/* ── Charger les compteurs de catégorie depuis le backend ──
     Refetch quand status ou search change (pas quand category change,
     car on veut continuer à voir tous les compteurs). */

  /* ── Lock body scroll when drawer open ── */
  useEffect(() => {
    if (drawerOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [drawerOpen]);

  /* ─────────────────────────────────────────────────────────────────────────
     DERIVED (memoized)
  ───────────────────────────────────────────────────────────────────────── */



  /* Catégories : dérivées des marchés actuellement chargés.
     Le filtrage par catégorie déclenche un refetch serveur. */
/* categoryList vient maintenant du backend.
     Si l'API n'a pas encore répondu, fallback sur les marchés chargés. */
const categoryList = useMemo(() => {
  // Backend OK → utiliser les vrais compteurs traduits
  if (categoryStats.categories && categoryStats.categories.length > 0) {
    return categoryStats.categories;
  }
  // Fallback : dériver des marchés chargés (pendant le 1er load)
  const counts = {};
  markets.forEach(m => {
    const c = m.category_name || "Other";
    counts[c] = (counts[c] || 0) + 1;
  });
  return Object.entries(counts)
    .map(([name, count]) => ({ name, name_source: name, count }))
    .sort((a, b) => b.count - a.count);
}, [markets, categoryStats]);

  /* Si la catégorie sélectionnée n'apparaît plus dans les résultats, reset */
useEffect(() => {
  if (
    category !== "ALL" &&
    !categoryList.some(c => (c.name_source || c.name) === category) &&
    !loading
  ) {
    setCategory("ALL");
  }
}, [categoryList, category, loading]);

  /* ── État pagination dérivé (mode hybride) ──
     - `visible` : ce qui est effectivement affiché à l'écran
     - `hasMoreInPage` : il reste des marchés CHARGÉS mais non révélés
     - `hasMoreServer` : il reste des marchés à fetch côté serveur
     - `hasMore` : l'un des deux → afficher le bouton "Voir plus"
  */
  const visible        = markets.slice(0, visibleCount);
  const hasMoreInPage  = visibleCount < markets.length;
  const hasMoreServer  = markets.length < totalCount;
  const hasMore        = hasMoreInPage || hasMoreServer;
  const remaining      = Math.max(0, totalCount - visible.length);
  const progressPct    = totalCount > 0 ? Math.min(100, (visible.length / totalCount) * 100) : 0;

  /* ─────────────────────────────────────────────────────────────────────────
     HANDLERS
  ───────────────────────────────────────────────────────────────────────── */

  const handleShowMore = useCallback(() => {
    if (loadingMore || !hasMore) return;

    // 1) D'abord révéler ce qui est déjà chargé mais caché (mode client-side)
    if (hasMoreInPage) {
      setVisibleCount(c => Math.min(c + PAGE_SIZE, markets.length));
      return;
    }

    // 2) Sinon fetcher la suite depuis le serveur (mode backend pagination)
    if (hasMoreServer) {
      setVisibleCount(c => c + PAGE_SIZE);   // aussi élargir la vue
      load({ append: true, offset: markets.length, limit: PAGE_SIZE });
    }
  }, [hasMore, hasMoreInPage, hasMoreServer, markets.length, loadingMore, load]);

  const handleShowAll = useCallback(() => {
    if (loadingMore || !hasMore) return;

    // Si le serveur a encore des marchés non chargés, on fetch tout d'un coup
    if (hasMoreServer) {
      load({ append: true, offset: markets.length, limit: remaining });
    }
    // Et on affiche tout ce qui est ou sera chargé
    setVisibleCount(totalCount);
  }, [hasMore, hasMoreServer, remaining, totalCount, markets.length, loadingMore, load]);

  const activeMobileFilters =
    (category !== "ALL" ? 1 : 0) +
    (sort     !== "volume" ? 1 : 0);

  const resetMobile = () => { setCategory("ALL"); setSort("volume"); };

  const fmtVol = (v) => {
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000)     return `${(v / 1_000).toFixed(0)}K`;
    return v.toFixed(0);
  };


  const stats = useMemo(() => {
  // Prio backend : valeurs globales respectant search.
  // Fallback local : somme des marchés actuellement chargés (cas où l'API n'a pas encore répondu).
  const apiVol  = categoryStats.total_volume;
  const apiOpen = categoryStats.open_count;

  const totalVol  = apiVol > 0
    ? apiVol
    : markets.reduce((s, m) => s + parseFloat(m.volume || 0), 0);

  const openCount = apiOpen > 0
    ? apiOpen
    : markets.filter(m => m.status === "OPEN").length;

  // isPartial : on affiche moins de marchés qu'il n'en existe dans la pool filtrée
  const isPartial = markets.length < totalCount;

  return { totalVol, openCount, isPartial };
}, [markets, totalCount, categoryStats]);

/* Total réel depuis le backend (respecte status + search).
     Fallback sur la somme des chips si l'API n'a pas encore répondu. */
const totalInPool = categoryStats.total > 0
  ? categoryStats.total
  : categoryList.reduce((s, c) => s + c.count, 0);

    const handleAnnouncementAction = useCallback((action) => {
    switch (action) {
      case "signup":
        // Ouvre ton modal d'inscription
        window.dispatchEvent(new CustomEvent("openAuth", { detail: { mode: "signup" } }));
        break;
      case "login":
        window.dispatchEvent(new CustomEvent("openAuth", { detail: { mode: "login" } }));
        break;
      case "leaderboard":
        // Navigation vers la page classement (si tu en as une)
        window.location.hash = "#leaderboard";
        break;
      case "wallet":
        window.location.hash = "#wallet";
        break;
      default:
        console.warn("Unknown announcement action:", action);
    }
  }, []);
  return (
    <view>
{ !user &&         <HeroCanvas
  user={user}
  stats={stats}
  total={categoryStats}
  onNavigate={onNavigate}
  onLoginClick={onLoginClick}
  t={t}
  openAuthModal={(mode) =>
    window.dispatchEvent(new CustomEvent("openAuth", { detail: { mode } }))
  }
/>}
    <Wrapper dir={dir}>
      {/* ── Hero ── */}

{ false &&     <Hero>
      
        <HeroTitle>
          {t("hero.title1")} <span>{t("hero.title2")}</span>
        </HeroTitle>
        <HeroSub>{t("hero.sub")}</HeroSub>
      {announcements.length > 0 && (
        <AnnouncementStack
          items={announcements}
          onAction={handleAnnouncementAction}
        />
      )}
        {user ? (
  <ReferralCallout
    role="button"
    tabIndex={0}
    onClick={() => {
      if (onNavigate) {
        onNavigate("settings", { tab: "referral" });
      } else {
        window.location.hash = "#settings/referral";
      }
    }}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        if (onNavigate) {
          onNavigate("settings", { tab: "referral" });
        } else {
          window.location.hash = "#settings/referral";
        }
      }
    }}
  >
    🤝 {t("hero.callout.user")}{" "}
    <ReferralLink>{t("hero.callout.link")} →</ReferralLink>
  </ReferralCallout>
) : (
  <EVPCallout>🎁 {t("hero.callout")}</EVPCallout>
)}
        <Stats>
          <Stat>
            <StatVal>
              {fmtVol(stats.totalVol)} <EVPBadge>EVP</EVPBadge>
              {stats.isPartial && <StatPartial title={t("stat.partial")}>+</StatPartial>}
            </StatVal>
            <StatLabel>{t("stat.volume")}</StatLabel>
          </Stat>
          <Stat>
            <StatVal>
              {stats.openCount}
              {stats.isPartial && <StatPartial title={t("stat.partial")}>+</StatPartial>}
            </StatVal>
            <StatLabel>{t("stat.open")}</StatLabel>
          </Stat>
          <Stat>
            <StatVal>{totalCount}</StatVal>
            <StatLabel>{t("stat.total")}</StatLabel>
          </Stat>
        </Stats>
      </Hero>
}
      {/* ── Mock fallback warning ── */}
      {usingMock && !loading && (
        <ErrorBanner>{t("loaderror")}</ErrorBanner>
      )}

      {/* ── Controls ── */}
      <Controls ref={controlsRef} className={controlsVisible ? "visible" : ""}>
        <SearchBox>
          <SearchIcon>⌕</SearchIcon>
          <SearchInput
            placeholder={t("search.ph")}
            value={search}
            onChange={e => setSearch(e.target.value)}
            dir={dir}
          />
          {searching && <SearchSpinner/>}
        </SearchBox>

        <Filters>
          {STATUS_KEYS.map((s, i) => (
            <FilterChip key={s} $a={status === s} onClick={() => setStatus(s)}>
              {t(STATUS_I18N[i])}
            </FilterChip>
          ))}
        </Filters>

        <SortBar>
          <SortLabel>{t("sort.label")}</SortLabel>
          <SortSelect value={sort} onChange={e => setSort(e.target.value)}>
            <option value="volume">{t("sort.volume")}</option>
            <option value="newest">{t("sort.newest")}</option>
            <option value="closing">{t("sort.closing")}</option>
          </SortSelect>
        </SortBar>

        <FilterFab
          $active={activeMobileFilters > 0}
          onClick={() => setDrawerOpen(true)}
        >
          ⚙ {t("filter.btn")}
          {activeMobileFilters > 0 && <FabBadge>{activeMobileFilters}</FabBadge>}
        </FilterFab>
      </Controls>

      {/* ── Category bar (desktop only) ── */}
      {!loading && categoryList.length > 1 && (
        <CategoryBar ref={categoryRef} className={categoryVisible ? "visible" : ""}>
          <CategoryLabel>{t("cat.label")}</CategoryLabel>
          <CategoryChips>
            <CategoryChip $a={category === "ALL"} onClick={() => setCategory("ALL")}>
              <ChipEmoji>✨</ChipEmoji>
              {t("cat.all")}
              <ChipCount $a={category === "ALL"}>{totalInPool}</ChipCount>
            </CategoryChip>
                {categoryList.map(c => {
                  const key = c.name_source || c.name;
                  return (
                    <CategoryChip
                      key={key}
                      $a={category === key}
                      onClick={() => setCategory(key)}
                    >
                      <ChipEmoji>{EMOJI_MAP[key] || "🎯"}</ChipEmoji>
                      {c.name}
                      <ChipCount $a={category === key}>{c.count}</ChipCount>
                    </CategoryChip>
                  );
                })}
          </CategoryChips>
        </CategoryBar>
      )}

      {/* ── Grid ── */}
      <Grid ref={gridRef} className={gridVisible ? "visible" : ""}>
        {loading
          ? Array.from({ length: INITIAL_VISIBLE }).map((_, i) => <Skeleton key={i}/>)
          : markets.length === 0
          ? <Empty>{t("empty")}</Empty>
          : visible.map((m, i) => (
              <MarketCard
                key={m.id}
                market={m}
                idx={i}
                onSelect={user ? onSelectMarket : () => onLoginClick("signup")}
                onTrade={onTradeMarket}
                style={{
                  animationDelay: gridVisible ? `${Math.min(i * 55, 400)}ms` : "0ms",
                  opacity: gridVisible ? undefined : 0,
                }}
              />
            ))
        }
        {/* Skeletons supplémentaires pendant "Voir plus" pour effet smooth */}
        {loadingMore && Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={`more-${i}`}/>
        ))}
      </Grid>

      {/* ── Zone "Voir plus" ── */}
      {!loading && markets.length > 0 && hasMore && (
        <ShowMoreWrap>
          <ShowMoreCount>
            <strong>{visible.length}</strong> / <strong>{totalCount}</strong>{" "}
            {t("showmore.label")}
          </ShowMoreCount>
          <ShowMoreProgress>
            <ShowMoreProgressFill $pct={progressPct}/>
          </ShowMoreProgress>
          <ShowMoreBtn onClick={handleShowMore} disabled={loadingMore}>
            {loadingMore
              ? t("showmore.loading")
              : <>
                  {t("showmore.btn")} (+{Math.min(PAGE_SIZE, remaining)})
                  <ShowMoreArrow>↓</ShowMoreArrow>
                </>
            }
          </ShowMoreBtn>
          {remaining > PAGE_SIZE && (
            <ShowAllBtn onClick={handleShowAll} disabled={loadingMore}>
              {t("showmore.all")} ({totalCount})
            </ShowAllBtn>
          )}
        </ShowMoreWrap>
      )}

      {/* ── Indicateur "Fin de la liste" ── */}
      {!loading && !hasMore && visible.length > INITIAL_VISIBLE && (
        <EndOfList>{t("showmore.end")}</EndOfList>
      )}

      {/* ── Mobile filter drawer ── */}
      <FilterDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        t={t}
        category={category}
        setCategory={setCategory}
        categoryList={categoryList}
        totalInPool={totalInPool}
        sort={sort}
        setSort={setSort}
        onReset={resetMobile}
      />
    </Wrapper>
    </view>
  );
}
